import { Clock } from 'lucide-react'
import { useServerTime } from '../../hooks/useServerTime'

interface ServerPacificTimeProps {
  compact?: boolean
}

const PACIFIC_TIMEZONE = 'America/Los_Angeles'

function formatPacificDateTime(isoDateTime?: string, compact = false): string {
  if (!isoDateTime) return compact ? 'PT --' : 'Server PT --'

  const date = new Date(isoDateTime)
  if (Number.isNaN(date.getTime())) return compact ? 'PT --' : 'Server PT --'

  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TIMEZONE,
    weekday: compact ? undefined : 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: compact ? undefined : '2-digit',
    hour12: true,
  }).format(date)

  return compact ? `PT ${formatted}` : `Server PT ${formatted}`
}

export function ServerPacificTime({ compact = false }: ServerPacificTimeProps) {
  const { serverTime, isLoading, error, refresh } = useServerTime({
    timezone: PACIFIC_TIMEZONE,
    refreshInterval: 15000,
  })

  const label = isLoading && !serverTime
    ? compact ? 'PT ...' : 'Server PT ...'
    : formatPacificDateTime(serverTime?.datetime, compact)

  return (
    <button
      type="button"
      onClick={refresh}
      className={`inline-flex items-center gap-1.5 rounded-md border border-soft bg-surface-alt text-secondary transition-colors hover:text-primary hover:bg-soft ${
        compact ? 'px-2 py-1 text-app-micro' : 'px-2.5 py-1.5 text-xs'
      }`}
      title={error ? `Server time unavailable: ${error}` : 'Server time in Pacific Time. Click to refresh.'}
      aria-label="Server time in Pacific Time"
    >
      <Clock className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
      <span className="whitespace-nowrap tabular-nums">{label}</span>
    </button>
  )
}
